import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import moment from 'moment';
import { Button, Dialog, LinearProgress } from '@material-ui/core';
import { apiBaseUrl } from '../../../config';

const STATUS_LABELS = {
    Building: 'Building',
    Completed: 'Completed',
    Failed: 'Failed',
    Cancelled: 'Cancelled',
};

const STATUS_COLORS = {
    Building: '#1976d2',
    Completed: '#2e7d32',
    Failed: '#d32f2f',
    Cancelled: '#757575',
};

const formatTime = (utc) => (utc ? moment.utc(utc).local().format('DD/MM/YYYY HH:mm:ss') : '-');

/** In-memory jobs cache build log of one company; follows the build while it is running. */
const JobsCacheBuildLogDialog = ({ open, companyId, companyName, headers, onClose }) => {
    const logRequest = useQuery(
        ['jobs-cache-build-log', companyId],
        async () => {
            const res = await axios.get(`${apiBaseUrl}/System/cache/jobs/${companyId}/log`, { headers });
            return res.data;
        },
        {
            enabled: open && !!companyId,
            refetchOnWindowFocus: false,
            retry: false,
            refetchInterval: (data) => (data?.status === 'Building' ? 3000 : false),
        },
    );

    const buildLog = logRequest.data;
    const total = buildLog?.totalJobs;
    const processed = buildLog?.processedJobs ?? 0;
    const percent = total ? Math.min(100, Math.round((processed / total) * 100)) : null;
    const entries = (buildLog?.entries || []).slice().reverse();
    const notFound = logRequest.error?.response?.status === 404;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="respModal autoHeight">
            <h1 className="modalTitle">Jobs cache build{companyName ? `: ${companyName}` : ''}</h1>

            {logRequest.isLoading && <LinearProgress />}

            {notFound && <p>No build log for this company. Logs are kept for one day after the build finishes.</p>}

            {logRequest.isError && !notFound && (
                <p style={{ color: STATUS_COLORS.Failed }}>
                    Failed to load the log: {logRequest.error?.response?.data?.message || logRequest.error?.message}
                </p>
            )}

            {buildLog && (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 16, rowGap: 4, marginBottom: 12 }}>
                        <span>Status</span>
                        <strong style={{ color: STATUS_COLORS[buildLog.status] }}>{STATUS_LABELS[buildLog.status] || buildLog.status}</strong>
                        <span>Processed</span>
                        <span>
                            {processed}{total != null ? ` of ${total} jobs` : ' jobs'}
                            {percent != null ? ` (${percent}%)` : ''}
                        </span>
                        <span>Started</span>
                        <span>{formatTime(buildLog.startedAtUtc)}</span>
                        <span>Last activity</span>
                        <span>
                            {formatTime(buildLog.lastActivityUtc)}
                            {buildLog.status === 'Building' && ` (${moment.utc(buildLog.lastActivityUtc).fromNow()})`}
                        </span>
                        {buildLog.finishedAtUtc && (
                            <>
                                <span>Finished</span>
                                <span>
                                    {formatTime(buildLog.finishedAtUtc)}
                                    {` (took ${moment.utc(moment.utc(buildLog.finishedAtUtc).diff(moment.utc(buildLog.startedAtUtc))).format('HH:mm:ss')})`}
                                </span>
                            </>
                        )}
                    </div>

                    {buildLog.status === 'Building' && (
                        <LinearProgress
                            variant={percent != null ? 'determinate' : 'indeterminate'}
                            value={percent ?? 0}
                            style={{ marginBottom: 12 }}
                        />
                    )}

                    <div
                        style={{
                            maxHeight: 360,
                            overflowY: 'auto',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            background: 'rgba(0, 0, 0, 0.04)',
                            padding: 8,
                            borderRadius: 4,
                        }}
                    >
                        {entries.map((entry, index) => (
                            <div key={`${entry.timeUtc}-${index}`}>
                                <span style={{ opacity: 0.6 }}>{formatTime(entry.timeUtc)}</span> {entry.message}
                            </div>
                        ))}
                    </div>
                </>
            )}

            <div className="btnHolder end fixedWidthBtn">
                <Button variant="outlined" color="primary" onClick={onClose}>
                    Close
                </Button>
            </div>
        </Dialog>
    );
};

export default JobsCacheBuildLogDialog;
